import React, { useEffect, useState } from 'react'
import { useNetworkStatus } from '../../utils/networkStatus'
import {
    StatusOfflineIcon,
    CheckCircleIcon,
    RefreshIcon,
} from '@heroicons/react/outline'
import clsx from 'clsx'
import { useFetchState } from '../../context/FetchStateContext'

export default function NetworkStatusAlert() {
    const [showStatus, setShowStatus] = useState(false)
    const [statusMessage, setStatusMessage] = useState('')
    const [currentStage, setCurrentStage] = useState('')
    const [syncStartTime, setSyncStartTime] = useState(null)

    const { isOffline } = useNetworkStatus()
    const { isFetching, dataFetched } = useFetchState()

    useEffect(() => {
        let syncingTimeout

        if (isOffline) {
            setShowStatus(true)
            setStatusMessage('No internet connection')
            setCurrentStage('offline')
        } else {
            if (showStatus) {
                if (currentStage === 'offline') {
                    setShowStatus(true)
                    setStatusMessage('Back online - Updating information')
                    setCurrentStage('syncing')
                    setSyncStartTime(Date.now())
                } else if (
                    !isFetching &&
                    dataFetched &&
                    currentStage === 'syncing'
                ) {
                    // Ensure "Syncing data" is shown for at least 3 seconds
                    const elapsed = Date.now() - syncStartTime
                    const delay = elapsed < 3000 ? 3000 - elapsed : 0

                    syncingTimeout = setTimeout(() => {
                        setStatusMessage('Information retrieved successfully')
                        setCurrentStage('synced')
                    }, delay)
                }
            }
        }

        return () => {
            clearTimeout(syncingTimeout)
        }
    }, [isOffline, isFetching, dataFetched, currentStage, syncStartTime])

    useEffect(() => {
        let successTimeout

        if (currentStage === 'synced') {
            successTimeout = setTimeout(() => {
                setStatusMessage('')
                setCurrentStage('')
                setShowStatus(false)
            }, 3000)
        }

        return () => {
            clearTimeout(successTimeout)
        }
    }, [currentStage])

    return (
        <div
            className={clsx(
                'w-full flex z-200 justify-center items-center gap-2 px-3 transition-all duration-150 ease-in-out',
                isOffline ? 'bg-bannerOffline' : 'bg-bannerOnline',
                showStatus ? 'max-h-6.5 py-1.5' : 'max-h-0 py-0',
            )}
            role="alert"
        >
            {showStatus && (
                <>
                    {isOffline && (
                        <StatusOfflineIcon className="h-3 w-3 text-white" />
                    )}
                    {!isOffline && currentStage === 'synced' && (
                        <CheckCircleIcon className="h-3 w-3 text-white" />
                    )}
                    {!isOffline && currentStage === 'syncing' && (
                        <div className="animate-spin">
                            <RefreshIcon
                                className="h-3 w-3 text-white"
                                style={{ transform: 'rotateY(180deg)' }}
                            />
                        </div>
                    )}

                    <p className={clsx('font-medium text-xs text-white')}>
                        {statusMessage}
                    </p>
                </>
            )}
        </div>
    )
}
